// Pure helpers for the Digital Twin > Battery Health "State of Health" section:
// the Latest snapshot / Monthly history switch and the SOH model fed to the
// generic MonthlyHistoricalPivotTable (months as columns, configured rows).
//
// Contract: GET /api/health/specific_battery/soh_history?batteryID&startDate&endDate
// returns raw { history: [sohML-shaped rows], forecast: [rulForecastML-shaped rows] };
// POST .../soh_history/actions is mail-only and takes the CSV rows built here.
// Everything else is decided in the UI from org meta
// batteryHealth.tables.MonthlyHistory (type, enabled, title, sectionTitle,
// pastMonths, futureMonths, levels, statusFrom, rows, filters, actions,
// statusLabels, headerIdentifier; Latest snapshot label in
// tables.LifecycleMetrics.viewLabel): the window, months, entity slots (from the
// Digital Twin battery tree), filters, status counts, the pivot and the CSV.
//
// Not-computed reasons arrive already mapped to UI text by the backend, so
// they are only parsed and de-duped here (never translated).

import { toTitleCase } from "../Formatters/TextFormatter";
import {
    formatMonthLabel,
    getMonthFirstDate,
    getMonthKeyOfDate,
    getMonthLastDate,
    shiftMonthKey,
} from "../DatePicker/DateConverter";
import { EMPTY_VALUE, formatPivotCsvValue } from "../Formatters/PivotValueFormatter";
import {
    getMetricCell,
    getValueCell,
    isForecastMonth,
    resolvePivotRows as resolveGenericPivotRows,
} from "../../Features/Table/MonthlyHistoricalPivotTable/pivotModelHelpers";

// Generic pivot model helpers live with the table; re-exported for SOH callers.
export { getMetricCell, getValueCell, isForecastMonth };

export const EMPTY_CELL = EMPTY_VALUE;

// ---------------------------------------------------------------------------
// Table types
// ---------------------------------------------------------------------------

export const TABLE_TYPE_GENERIC = "generic";
export const TABLE_TYPE_MONTHLY_PIVOT = "monthlyHistoricalPivot";
const KNOWN_TABLE_TYPES = [TABLE_TYPE_GENERIC, TABLE_TYPE_MONTHLY_PIVOT];

/** tables.<X>.type: absent / unknown -> "generic" (GenericDraggableTableView). */
export const resolveTableType = (tableConfig) =>
    KNOWN_TABLE_TYPES.includes(tableConfig?.type) ? tableConfig.type : TABLE_TYPE_GENERIC;

// Table types that can render the Monthly history view. The GenericDraggableTable
// rendering was removed, so only the pivot type shows the switch.
export const MONTHLY_HISTORY_VIEW_TYPES = [TABLE_TYPE_MONTHLY_PIVOT];

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export const SOH_HISTORY_STATUSES = ["computed", "not_computed", "forecast"];
export const SOH_CONFIDENCE_LEVELS = ["High", "Medium", "Low"];
export const SOH_ENTITY_LEVELS = ["battery", "string", "pack"];

export const MONTHLY_FILTER_KEYS = ["status", "entity", "confidence"];
// Row selection is not user-facing: a meta "rowSelection" entry is ignored and
// exports always cover every row.
export const MONTHLY_ACTION_KEYS = ["download", "email"];

// Filter values meaning "no filter".
export const ALL_FILTER = "all";
export const ANY_CONFIDENCE = "any";

const MONTH_WINDOW_MAX = 12;
export const DEFAULT_PAST_MONTHS = 6;
export const DEFAULT_FUTURE_MONTHS = 6;

/** Default rows (JBM set) when org meta provides none. */
export const DEFAULT_PIVOT_ROWS = [
    { key: "status", type: "status", label: "Status" },
    { key: "computedOn", type: "metric", label: "Computed on", past: "date", format: "date" },
    { key: "entities", type: "entities", label: "SOH", past: "sohEst", future: "SOHForecasted", subPast: "sohConfidence", format: "percent" },
    { key: "odometer", type: "metric", label: "Odometer (km)", past: "distanceTravelledKm", format: "km" },
];

export const DEFAULT_STATUS_LABELS = {
    computed: "Computed",
    not_computed: "Not computed",
    forecast: "Predicted",
};


const DEFAULT_TITLE = "State of Health";
const DEFAULT_LATEST_LABEL = "Latest snapshot";
const DEFAULT_MONTHLY_LABEL = "Monthly history";

const isPlainObject = (value) =>
    value !== null && typeof value === "object" && !Array.isArray(value);

const nonEmptyString = (value) => typeof value === "string" && value.trim() !== "";

// Overlay string values from meta onto defaults; anything else in meta is ignored.
const mergeLabels = (defaults, overrides) => {
    const merged = { ...defaults };
    if (!isPlainObject(overrides)) return merged;
    Object.entries(overrides).forEach(([key, value]) => {
        if (nonEmptyString(value)) merged[key] = value;
    });
    return merged;
};

/**
 * A meta list of switches (filters / actions): omitted (not an array) = all
 * on; otherwise only the listed known keys are on.
 */
export const resolveSwitchList = (list, keys) => {
    const on = Array.isArray(list) ? list : keys;
    return keys.reduce((acc, key) => {
        acc[key] = on.includes(key);
        return acc;
    }, {});
};

// Integer 0..12 or undefined (undefined = the default window).
export const resolveMonthWindow = (value) => {
    if (value === null || value === undefined || value === "") return undefined;
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0 || n > MONTH_WINDOW_MAX) return undefined;
    return n;
};

/** Rows config (see pivotModelHelpers.resolvePivotRows) with the SOH defaults. */
export const resolvePivotRows = (rows) => resolveGenericPivotRows(rows, DEFAULT_PIVOT_ROWS);

/** Valid levels in a fixed order, or undefined when none are given / none valid. */
const resolveLevels = (levels) => {
    if (!Array.isArray(levels)) return undefined;
    const lowered = levels.filter((l) => typeof l === "string").map((l) => l.trim().toLowerCase());
    const ordered = SOH_ENTITY_LEVELS.filter((level) => lowered.includes(level));
    return ordered.length ? ordered : undefined;
};

/**
 * Resolve batteryHealth.tables.MonthlyHistory (+ LifecycleMetrics.viewLabel /
 * headerIdentifier, and visibleBatteryParts for the default levels). Entity
 * labels come from the Digital Twin battery tree (buildEntitySlots); reasons
 * arrive already mapped by the backend. `enabled` is true only for an
 * explicit enabled === true. `showSwitch` additionally needs a table type that
 * can render the view (MONTHLY_HISTORY_VIEW_TYPES).
 */
export const resolveMonthlyHistoryConfig = (batteryHealthConfig) => {
    // Levels: meta levels, else batteryHealth.visibleBatteryParts ∩ levels, else all.
    const metaLevels = resolveLevels(batteryHealthConfig?.tables?.MonthlyHistory?.levels);
    const levels = metaLevels || resolveLevels(batteryHealthConfig?.visibleBatteryParts) || [...SOH_ENTITY_LEVELS];
    const rawStatusFrom = batteryHealthConfig?.tables?.MonthlyHistory?.statusFrom;
    const statusFrom = SOH_ENTITY_LEVELS.includes(rawStatusFrom) ? rawStatusFrom : levels[0];
    const tables = isPlainObject(batteryHealthConfig?.tables) ? batteryHealthConfig.tables : {};
    const monthly = isPlainObject(tables.MonthlyHistory) ? tables.MonthlyHistory : {};
    const lifecycle = isPlainObject(tables.LifecycleMetrics) ? tables.LifecycleMetrics : {};
    const headerIdentifier = isPlainObject(monthly.headerIdentifier)
        ? monthly.headerIdentifier
        : (isPlainObject(lifecycle.headerIdentifier) ? lifecycle.headerIdentifier : undefined);
    const enabled = monthly.enabled === true;
    const type = resolveTableType(monthly);

    return {
        enabled,
        type,
        showSwitch: enabled && MONTHLY_HISTORY_VIEW_TYPES.includes(type),
        title: nonEmptyString(monthly.sectionTitle) ? monthly.sectionTitle : DEFAULT_TITLE,
        latestSnapshotLabel: nonEmptyString(lifecycle.viewLabel) ? lifecycle.viewLabel : DEFAULT_LATEST_LABEL,
        monthlyHistoryLabel: nonEmptyString(monthly.title) ? monthly.title : DEFAULT_MONTHLY_LABEL,
        pastMonths: resolveMonthWindow(monthly.pastMonths) ?? DEFAULT_PAST_MONTHS,
        futureMonths: resolveMonthWindow(monthly.futureMonths) ?? DEFAULT_FUTURE_MONTHS,
        rows: resolvePivotRows(monthly.rows),
        levels,
        statusFrom,
        statusLabels: mergeLabels(DEFAULT_STATUS_LABELS, monthly.statusLabels),
        filters: resolveSwitchList(monthly.filters, MONTHLY_FILTER_KEYS),
        actions: resolveSwitchList(monthly.actions, MONTHLY_ACTION_KEYS),
        headerIdentifier,
    };
};

// ---------------------------------------------------------------------------
// Reasons
// ---------------------------------------------------------------------------

/**
 * sohNotComputedReasons arrives as a list, a JSON string of a list, a single
 * string, or null. Always returns an array of non-empty strings.
 */
export const parseReasonList = (value) => {
    if (Array.isArray(value)) {
        return value.filter(nonEmptyString).map((reason) => reason.trim());
    }
    if (nonEmptyString(value)) {
        const trimmed = value.trim();
        if (trimmed.startsWith("[")) {
            try {
                const parsed = JSON.parse(trimmed);
                if (Array.isArray(parsed)) return parseReasonList(parsed);
            } catch (e) {
                // Not JSON: treat as a single reason.
            }
        }
        return [trimmed];
    }
    return [];
};

/** Parsed + de-duped (order kept). Never re-mapped: the backend already mapped them. */
export const dedupeReasons = (value) => Array.from(new Set(parseReasonList(value)));

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------

/** "high" / "HIGH" -> "High" (title case, as in the CSV "(High)"); empty -> null. */
export const formatConfidence = (value) => {
    if (!nonEmptyString(value)) return null;
    const v = value.trim().toLowerCase();
    return v.charAt(0).toUpperCase() + v.slice(1);
};

// Confidence second lines are title-cased on screen to match the CSV.
const withConfidenceCase = (cell) =>
    cell?.subField === "sohConfidence" && cell.subText
        ? { ...cell, subText: formatConfidence(cell.subText) }
        : cell;

/** Month status: forecast months are "forecast"; history is computed or not. */
export const getMonthStatus = (month) => {
    if (isForecastMonth(month)) return "forecast";
    return month?.status === "computed" ? "computed" : "not_computed";
};

/** Status cell: chip label + reasons (reasons only for not computed). */
export const getStatusCell = (month, statusLabels) => {
    const status = getMonthStatus(month);
    const labels = mergeLabels(DEFAULT_STATUS_LABELS, statusLabels);
    return {
        kind: "status",
        status,
        text: labels[status],
        reasons: status === "not_computed" ? dedupeReasons(month?.reasons) : [],
    };
};

/**
 * Entity row cell.
 *   no row for the month         -> { kind: "empty", text: "—" }
 *   history, not computed        -> { kind: "not_computed", text: "—", reasons }
 *   otherwise                    -> value cell (past / future field)
 */
export const getEntityCell = (row, entity, month) => {
    const value = entity?.values?.[month?.month];
    if (!isPlainObject(value)) return { kind: "empty", text: EMPTY_CELL };
    if (!isForecastMonth(month) && value.status !== "computed") {
        return { kind: "not_computed", text: EMPTY_CELL, reasons: dedupeReasons(value.reasons) };
    }
    return withConfidenceCase(getValueCell(row, month, value));
};

// ---------------------------------------------------------------------------
// Window
// ---------------------------------------------------------------------------

/**
 * The fetch window around `currentMonth` ("YYYY-MM", in the user's timezone):
 * history months currentMonth - pastMonths + 1 .. currentMonth, forecast months
 * currentMonth + 1 .. currentMonth + futureMonths. startDate is the 1st of the
 * first month, endDate the last day of the last month. Returns null when there
 * is no month at all (both 0) or currentMonth is invalid.
 */
export const getSohHistoryWindow = ({ currentMonth, pastMonths = DEFAULT_PAST_MONTHS, futureMonths = DEFAULT_FUTURE_MONTHS } = {}) => {
    const past = resolveMonthWindow(pastMonths) ?? DEFAULT_PAST_MONTHS;
    const future = resolveMonthWindow(futureMonths) ?? DEFAULT_FUTURE_MONTHS;
    if (!shiftMonthKey(currentMonth, 0) || past + future === 0) return null;
    const startMonth = past > 0 ? shiftMonthKey(currentMonth, -(past - 1)) : shiftMonthKey(currentMonth, 1);
    const endMonth = shiftMonthKey(currentMonth, future);
    return {
        currentMonth,
        startMonth,
        endMonth: future > 0 ? endMonth : currentMonth,
        startDate: getMonthFirstDate(startMonth),
        endDate: getMonthLastDate(future > 0 ? endMonth : currentMonth),
        pastMonths: past,
        futureMonths: future,
    };
};

/** GET params: battery + window dates. Filters are applied client-side. */
export const buildSohHistoryParams = ({ batteryID, window } = {}) => ({
    batteryID,
    startDate: window?.startDate,
    endDate: window?.endDate,
});

// ---------------------------------------------------------------------------
// Entity slots (Digital Twin battery tree)
// ---------------------------------------------------------------------------

export const ENTITY_SLOT_LABELS = { battery: "Battery System", string: "String", pack: "Pack" };

// Same i18n keys the Digital Twin sidebar labels its nodes with
// (transformTwinHierarchyData: `${t("digital_twin:<level>")} <n>`).
const LEVEL_LABEL_KEYS = { string: "digital_twin:string", pack: "digital_twin:pack" };
const defaultTranslate = (key) =>
    ({ "digital_twin:string": "String", "digital_twin:pack": "Pack" }[key] || key);

const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

/** Trailing number of a tree label ("String 3" -> 3), else null. */
const trailingNumber = (label) => {
    const match = typeof label === "string" ? label.match(/(\d+)\s*$/) : null;
    return match ? Number(match[1]) : null;
};

/**
 * Slot number of a tree node (must equal the backend's entityNumber):
 *   string - info.stringNo, else the numeric node id (BATTERY_SYSTEM string ids
 *            are the stringNo), else the label's number
 *   pack   - info.absPackNo only (packConfig.absPackNo, sent on BATTERY_STRUCTURE
 *            pack nodes by battery_config/v3). No label fallback: the label's
 *            number is a display sequence that can differ from absPackNo.
 * Returns null when there is no reliable number.
 */
export const getTreeNodeNumber = (node, level) => {
    if (level === "string") {
        return toNumberOrNull(node?.info?.stringNo) ?? toNumberOrNull(node?.id) ?? trailingNumber(node?.label);
    }
    if (level === "pack") return toNumberOrNull(node?.info?.absPackNo);
    return null;
};

const collectTreeNodes = (nodes, level) => {
    const found = [];
    const walk = (list) => (Array.isArray(list) ? list : []).forEach((node) => {
        if ((node?.level || "").toLowerCase() === level) found.push(node);
        walk(node?.children);
    });
    walk(nodes);
    return found;
};

export const slotKey = (entityType, entityNumber) =>
    entityType === "battery" ? "entity:battery" : `entity:${entityType}:${entityNumber}`;

/** Slot label like the sidebar's: toTitleCase(`${t("digital_twin:<level>")} <n>`). */
const levelLabel = (translate, level, number) =>
    toTitleCase(`${translate(LEVEL_LABEL_KEYS[level])} ${number}`);

/**
 * Entity slots for the given levels, in order battery, strings, packs:
 *   battery - always one slot, labelled t("Battery System") (never the tree's
 *             battery node label, which is a display name / id)
 *   string  - string nodes of the BATTERY_SYSTEM tree ("String N")
 *   pack    - pack nodes of the BATTERY_STRUCTURE tree ("Pack N", N = absPackNo);
 *             a pack node without absPackNo becomes a slot keyed by its node id,
 *             shown with its tree label, that no data row can map to
 * Labels go through `t` with the sidebar's keys (default: English), so the
 * rows, the Entity dropdown and the CSV share them. Slots exist even without
 * data. When a tree has no node of a level (not loaded / not modelled), that
 * level's slots come from the data rows instead.
 */
export const buildEntitySlots = ({ batterySystem, batteryStructure, levels = SOH_ENTITY_LEVELS, rows = [], t } = {}) => {
    const translate = typeof t === "function" ? t : defaultTranslate;
    const wanted = resolveLevels(levels) || [...SOH_ENTITY_LEVELS];
    const numberedSlot = (level, n) => ({ entityType: level, entityNumber: n, rowKey: slotKey(level, n), label: levelLabel(translate, level, n) });
    const fromData = (level) => {
        const numbers = [];
        (Array.isArray(rows) ? rows : []).forEach((row) => {
            const n = toNumberOrNull(row?.entityNumber);
            if (row?.entityType === level && n !== null && !numbers.includes(n)) numbers.push(n);
        });
        return numbers.sort((a, b) => a - b).map((n) => numberedSlot(level, n));
    };
    const slots = [];
    wanted.forEach((level) => {
        if (level === "battery") {
            // Always t("Battery System"), as in Latest snapshot. Never the tree's
            // battery node label: that is a display name / id, not user-facing here.
            slots.push({
                entityType: "battery",
                entityNumber: null,
                rowKey: slotKey("battery", null),
                label: translate(ENTITY_SLOT_LABELS.battery),
            });
            return;
        }
        const nodes = collectTreeNodes(level === "string" ? batterySystem : batteryStructure, level);
        if (!nodes.length) {
            slots.push(...fromData(level));
            return;
        }
        const numbered = [];
        const unnumbered = [];
        nodes.forEach((node) => {
            const n = getTreeNodeNumber(node, level);
            if (n === null) {
                unnumbered.push({ entityType: level, entityNumber: null, rowKey: `entity:${level}:id${node?.id}`, label: node?.label || String(node?.id) });
            } else if (!numbered.some((slot) => slot.entityNumber === n)) {
                numbered.push(numberedSlot(level, n));
            }
        });
        slots.push(...numbered.sort((a, b) => a.entityNumber - b.entityNumber), ...unnumbered);
    });
    return slots;
};

/**
 * The level that owns the month status: statusFrom, unless that level has no
 * slot and no row, then the first level (in levels order) that has either.
 */
export const resolveStatusFrom = ({ statusFrom, levels, slots, rows }) => {
    const has = (level) =>
        (Array.isArray(slots) ? slots : []).some((s) => s.entityType === level && s.entityNumber !== null) ||
        (level === "battery" && (Array.isArray(slots) ? slots : []).some((s) => s.entityType === "battery")) ||
        (Array.isArray(rows) ? rows : []).some((r) => r?.entityType === level);
    if (has(statusFrom)) return statusFrom;
    const fallback = (resolveLevels(levels) || SOH_ENTITY_LEVELS).find(has);
    return fallback || statusFrom;
};

/** The slot a data row belongs to (entityType + entityNumber; battery has one slot). */
export const rowSlotKey = (row) => {
    if (row?.entityType === "battery") return slotKey("battery", null);
    const n = toNumberOrNull(row?.entityNumber);
    return n === null ? null : slotKey(row?.entityType, n);
};

// ---------------------------------------------------------------------------
// Months and entity values (client-side, from raw rows)
// ---------------------------------------------------------------------------

/**
 * Latest row per slot per month: { [slotKey]: { [month]: row } }. Rows outside
 * the given month set or without a slot are dropped.
 */
const latestRowsBySlotMonth = (rows, monthSet) => {
    const out = {};
    (Array.isArray(rows) ? rows : []).forEach((row) => {
        const key = rowSlotKey(row);
        const month = getMonthKeyOfDate(row?.date);
        if (!key || !month || !monthSet.has(month)) return;
        out[key] = out[key] || {};
        const prev = out[key][month];
        if (!prev || String(row.date) >= String(prev.date)) out[key][month] = row;
    });
    return out;
};

const monthsBetween = (start, end) => {
    const list = [];
    let m = start;
    while (m && end && m <= end) {
        list.push(m);
        m = shiftMonthKey(m, 1);
    }
    return list;
};

/**
 * The month entity: the picked entity when one is chosen in the Entity filter,
 * else the statusFrom level's first slot (the battery for "battery").
 */
export const getMonthEntityKey = ({ slots, statusFrom, entityFilter }) => {
    const picked = parseEntityFilter(entityFilter);
    if (picked) return slotKey(picked.entityType, picked.entityNumber);
    const first = (Array.isArray(slots) ? slots : []).find((slot) => slot.entityType === statusFrom);
    return first ? first.rowKey : slotKey(statusFrom || "battery", null);
};

const toEntityValue = (row, kind) => {
    if (!row) return undefined;
    if (kind === "forecast") return { ...row, status: "forecast" };
    return { ...row, status: row.status === "computed" ? "computed" : "not_computed", reasons: dedupeReasons(row.sohNotComputedReasons) };
};

/**
 * Build the screen data from the raw API rows:
 *   history months: months of the window up to currentMonth where the month
 *                   entity has a history row (its latest row that month)
 *   forecast months: months after currentMonth with any forecast row of the
 *                    shown entities (latest row per entity per month)
 * Then the client-side filters: Entity (level / one entity; a picked entity is
 * also the month entity), Confidence (history months by the month entity's
 * sohConfidence, case-insensitive; forecast months kept) and Status (months by
 * status). statusCounts are counted after every filter except Status.
 * Returns { months, entities, statusCounts } where months / entities have the
 * shape buildSohPivotModel takes.
 */
export const buildSohHistoryView = ({
    history = [],
    forecast = [],
    slots = [],
    window,
    statusFrom = "battery",
    levels,
    filters = {},
} = {}) => {
    if (!window) return { months: [], entities: [], statusCounts: { all: 0, computed: 0, not_computed: 0, forecast: 0 } };
    const pastMonths = monthsBetween(window.startMonth, window.currentMonth);
    const futureMonths = monthsBetween(shiftMonthKey(window.currentMonth, 1), window.endMonth);
    const historyBySlot = latestRowsBySlotMonth(history, new Set(pastMonths));
    const forecastBySlot = latestRowsBySlotMonth(forecast, new Set(futureMonths));

    // Entity filter: a level narrows the entity rows; a pick narrows to one slot.
    const entityFilter = filters?.entity;
    const picked = parseEntityFilter(entityFilter);
    const level = typeof entityFilter === "string" && entityFilter.startsWith("level:") ? entityFilter.slice("level:".length) : null;
    // `slots` may include the statusFrom level's slots for the month entity;
    // only the configured levels are shown as entity rows.
    const shownLevels = resolveLevels(levels);
    let shownSlots = (Array.isArray(slots) ? slots : []).filter((slot) => !shownLevels || shownLevels.includes(slot.entityType));
    if (picked) shownSlots = shownSlots.filter((slot) => slot.rowKey === slotKey(picked.entityType, picked.entityNumber));
    else if (level) shownSlots = shownSlots.filter((slot) => slot.entityType === level);

    const monthEntityKey = getMonthEntityKey({ slots, statusFrom, entityFilter });
    const monthEntityHistory = historyBySlot[monthEntityKey] || {};
    const monthEntityForecast = forecastBySlot[monthEntityKey] || {};

    let months = [
        ...pastMonths.filter((m) => monthEntityHistory[m]).map((m) => {
            const row = monthEntityHistory[m];
            const status = row.status === "computed" ? "computed" : "not_computed";
            return {
                month: m,
                kind: "history",
                status,
                reasons: status === "not_computed" ? dedupeReasons(row.sohNotComputedReasons) : [],
                values: row,
            };
        }),
        ...futureMonths
            .filter((m) => shownSlots.some((slot) => forecastBySlot[slot.rowKey]?.[m]) || monthEntityForecast[m])
            .map((m) => ({ month: m, kind: "forecast", status: "forecast", reasons: [], values: monthEntityForecast[m] || {} })),
    ];

    const confidence = filters?.confidence;
    if (nonEmptyString(confidence) && confidence !== ANY_CONFIDENCE) {
        const wanted = confidence.trim().toLowerCase();
        months = months.filter((m) => m.kind === "forecast" || String(m.values?.sohConfidence || "").toLowerCase() === wanted);
    }

    const statusCounts = SOH_HISTORY_STATUSES.reduce(
        (acc, status) => ({ ...acc, [status]: months.filter((m) => m.status === status).length }),
        { all: months.length }
    );

    const status = filters?.status;
    if (SOH_HISTORY_STATUSES.includes(status)) months = months.filter((m) => m.status === status);

    const entities = shownSlots.map((slot) => {
        const values = {};
        months.forEach((m) => {
            const row = m.kind === "forecast" ? forecastBySlot[slot.rowKey]?.[m.month] : historyBySlot[slot.rowKey]?.[m.month];
            const value = toEntityValue(row, m.kind);
            if (value) values[m.month] = value;
        });
        return { ...slot, values };
    });

    return { months, entities, statusCounts };
};

// ---------------------------------------------------------------------------
// Entities (filter options)
// ---------------------------------------------------------------------------

/** Entity row label: the slot label, else "<type label> <number>". */
export const getEntityLabel = (entity) => {
    if (nonEmptyString(entity?.label)) return entity.label;
    const base = ENTITY_SLOT_LABELS[entity?.entityType] || entity?.entityType || "";
    if (entity?.entityType === "battery") return base;
    const number = entity?.entityNumber;
    return number === null || number === undefined || number === "" ? base : `${base} ${number}`;
};

/** Row key of an entity row: its slot key ("entity:battery", "entity:string:2", "entity:pack:7"). */
export const entityRowKey = (entity) =>
    nonEmptyString(entity?.rowKey) ? entity.rowKey : slotKey(entity?.entityType, entity?.entityNumber);

// Entity select values (UI only): "all", "level:battery", or
// "pick:<entityType>:<entityNumber>" for one string / pack slot.
export const levelFilterValue = (entityType) => `level:${entityType}`;
export const entityFilterValue = (entity) => `pick:${entity?.entityType}:${entity?.entityNumber}`;

/**
 * Entity select options from the slots: All, Battery System (as a level), then
 * each numbered string / pack (a slot without a number has no data to narrow to).
 */
export const buildEntityOptions = (slots, allLabel = "All") => {
    const options = [{ value: ALL_FILTER, label: allLabel }];
    (Array.isArray(slots) ? slots : []).forEach((slot) => {
        if (!slot?.entityType) return;
        if (slot.entityType !== "battery" && (slot.entityNumber === null || slot.entityNumber === undefined)) return;
        const value = slot.entityType === "battery" ? levelFilterValue("battery") : entityFilterValue(slot);
        if (!options.some((o) => o.value === value)) options.push({ value, label: getEntityLabel(slot) });
    });
    return options;
};

/** Entity filter shows when enabled in meta and there is more than one entity slot. */
export const shouldShowEntityFilter = (filterEnabled, slots) =>
    Boolean(filterEnabled) && (Array.isArray(slots) ? slots.length : 0) > 1;

/**
 * The entity chosen in the Entity select as { entityType, entityNumber }, or
 * null for "all", a level, or a value whose number is not an integer.
 */
export const parseEntityFilter = (entityFilter) => {
    if (typeof entityFilter !== "string" || !entityFilter.startsWith("pick:")) return null;
    const [entityType, rawNumber] = entityFilter.slice("pick:".length).split(":");
    const n = Number(rawNumber);
    if (!entityType || rawNumber === undefined || rawNumber === "" || !Number.isInteger(n)) return null;
    return { entityType, entityNumber: n };
};

/** Analytics-safe level of an Entity select value: "all" | "battery" | "string" | "pack". */
export const getEntityFilterLevel = (entityFilter) => {
    if (typeof entityFilter === "string" && entityFilter.startsWith("level:")) return entityFilter.slice("level:".length);
    const picked = parseEntityFilter(entityFilter);
    return picked ? picked.entityType : ALL_FILTER;
};

// ---------------------------------------------------------------------------
// Model for MonthlyHistoricalPivotTable
// ---------------------------------------------------------------------------

/**
 * Build { columns, rows } for the generic pivot table from buildSohHistoryView's
 * { months, entities } and the rows config.
 *   columns: months oldest -> newest ({ key, label, isForecast })
 *   rows:    status / metric rows one each; the (single) entities row expands
 *            to one row per shown entity slot (battery, strings, packs), keyed
 *            by the slot key. Slots with no data for a month render "—".
 */
export const buildSohPivotModel = ({ view, rowsConfig, statusLabels } = {}) => {
    const months = (Array.isArray(view?.months) ? view.months : []).filter((m) => m?.month);
    const rowsCfg = resolvePivotRows(rowsConfig);
    const entities = Array.isArray(view?.entities) ? view.entities : [];

    const columns = months.map((month) => ({
        key: month.month,
        label: formatMonthLabel(month.month),
        isForecast: isForecastMonth(month),
    }));

    const cellsFor = (getCell) =>
        months.reduce((acc, month) => {
            acc[month.month] = getCell(month);
            return acc;
        }, {});

    const rows = [];
    rowsCfg.forEach((row) => {
        if (row.type === "status") {
            rows.push({ key: row.key, rowType: "status", label: row.label || row.key, cells: cellsFor((m) => getStatusCell(m, statusLabels)) });
        } else if (row.type === "metric") {
            rows.push({ key: row.key, rowType: "metric", label: row.label || row.key, cells: cellsFor((m) => withConfidenceCase(getMetricCell(row, m))) });
        } else if (row.type === "entities") {
            entities.forEach((entity) => {
                rows.push({
                    key: entityRowKey(entity),
                    rowType: "entity",
                    entityType: entity.entityType,
                    label: getEntityLabel(entity),
                    subLabel: row.label || null,
                    cells: cellsFor((m) => getEntityCell(row, entity, m)),
                });
            });
        }
    });

    return { columns, rows };
};

// ---------------------------------------------------------------------------
// CSV (client-side download + the mail action's rows)
// ---------------------------------------------------------------------------

const CSV_METRIC_COLUMN = "Metric";

/** Raw value + sub value of a row for a month (past* on history, future* on forecast). */
const rawValueOf = (row, month, values) => {
    const forecast = isForecastMonth(month);
    const field = forecast ? row?.future : row?.past;
    const subField = forecast ? row?.subFuture : row?.subPast;
    const source = isPlainObject(values) ? values : {};
    return { value: field ? source[field] : null, sub: subField ? source[subField] : null, subField };
};

const csvSub = (sub, subField) => {
    if (sub === null || sub === undefined || sub === "") return "";
    return subField === "sohConfidence" ? formatConfidence(String(sub)) : String(sub);
};

const csvValueWithSub = (row, month, values) => {
    const { value, sub, subField } = rawValueOf(row, month, values);
    const text = formatPivotCsvValue(value, row?.format);
    if (text === "") return "";
    const subText = csvSub(sub, subField);
    return subText ? `${text} (${subText})` : text;
};

const notComputedText = (labels, reasons) =>
    reasons.length ? `${labels.not_computed} - ${reasons.join("; ")}` : labels.not_computed;

/**
 * CSV rows with the same months, rows and filters as the screen:
 *   first column  identifier label -> value (e.g. "Bus Number": "KA01AB1234"), when a label is given
 *   "Metric"      row label (entity rows "String 2 (SOH)")
 *   one column per month, "Aug 2026"; forecast "Dec 2026 (Predicted)"
 * Cells: status "Computed" / "Not computed - r1; r2" / "Predicted"; dates
 * "31 Aug 2026"; percent one decimal (half-up); km whole number; number whole;
 * age "1 yr 4 mo"; entity cells "86.4% (High)", not computed "Not computed - r1; r2";
 * no value -> "".
 */
export const buildSohHistoryCsvRows = ({ view, rowsConfig, statusLabels, identifier } = {}) => {
    const labels = mergeLabels(DEFAULT_STATUS_LABELS, statusLabels);
    const months = (Array.isArray(view?.months) ? view.months : []).filter((m) => m?.month);
    const entities = Array.isArray(view?.entities) ? view.entities : [];
    const header = (m) => (isForecastMonth(m) ? `${formatMonthLabel(m.month)} (${labels.forecast})` : formatMonthLabel(m.month));
    const idColumn = nonEmptyString(identifier?.label) ? identifier.label : null;
    const idValue = identifier?.value === null || identifier?.value === undefined ? "" : identifier.value;

    const makeRow = (label, cellFor) => {
        const out = {};
        if (idColumn) out[idColumn] = idValue;
        out[CSV_METRIC_COLUMN] = label;
        months.forEach((m) => {
            out[header(m)] = cellFor(m);
        });
        return out;
    };

    const rows = [];
    resolvePivotRows(rowsConfig).forEach((row) => {
        const rowLabel = row.label || row.key;
        if (row.type === "status") {
            rows.push(makeRow(rowLabel, (m) => {
                const status = getMonthStatus(m);
                return status === "not_computed" ? notComputedText(labels, dedupeReasons(m.reasons)) : labels[status];
            }));
        } else if (row.type === "metric") {
            rows.push(makeRow(rowLabel, (m) => csvValueWithSub(row, m, m.values)));
        } else if (row.type === "entities") {
            entities.forEach((entity) => {
                const name = getEntityLabel(entity);
                rows.push(makeRow(row.label ? `${name} (${row.label})` : name, (m) => {
                    const value = entity?.values?.[m.month];
                    if (!isPlainObject(value)) return "";
                    if (!isForecastMonth(m) && value.status !== "computed") {
                        return notComputedText(labels, dedupeReasons(value.reasons));
                    }
                    return csvValueWithSub(row, m, value);
                }));
            });
        }
    });
    return rows;
};

// ---------------------------------------------------------------------------
// Footer, actions, filter options
// ---------------------------------------------------------------------------

/**
 * Footer parts ("Showing N months between Jun 2026 and May 2027"), or null.
 * Months can be non-contiguous after filtering, so this is the first and last
 * shown month, not a continuous range.
 */
export const getMonthRangeSummary = (months) => {
    const list = (Array.isArray(months) ? months : []).filter((m) => m?.month);
    if (!list.length) return null;
    return {
        count: list.length,
        start: formatMonthLabel(list[0].month),
        end: formatMonthLabel(list[list.length - 1].month),
    };
};

/** Download / Email are usable only with data on screen and no fetch in flight. */
export const canRunSohHistoryActions = ({ hasMonths, isLoading } = {}) =>
    Boolean(hasMonths) && !isLoading;

/** Status chip options with counts (null when a count is absent). */
export const buildStatusFilterOptions = (statusCounts, statusLabels, allLabel = "All") => {
    const labels = mergeLabels(DEFAULT_STATUS_LABELS, statusLabels);
    const countOf = (key) => {
        const n = Number(statusCounts?.[key]);
        return statusCounts && statusCounts[key] !== undefined && statusCounts[key] !== null && !isNaN(n) ? n : null;
    };
    return [
        { value: ALL_FILTER, label: allLabel, count: countOf("all") },
        ...SOH_HISTORY_STATUSES.map((status) => ({ value: status, label: labels[status], count: countOf(status) })),
    ];
};

/**
 * POST body for the mail action: actionType "mail", the battery (access
 * scoping only), the CSV rows built here, and the email fields.
 */
export const buildSohHistoryMailBody = ({ batteryID, rows, emailTo, emailCc, subject, body, fileName } = {}) => ({
    actionType: "mail",
    batteryID,
    rows: Array.isArray(rows) ? rows : [],
    emailTo: Array.isArray(emailTo) ? emailTo : [],
    emailCc: Array.isArray(emailCc) ? emailCc : [],
    subject,
    body,
    fileName,
});
